import React, {useMemo, useState} from 'react';
import {
    Target,
    Lightbulb,
    FileText,
    Palette,
    Globe,
    Share2,
    Code,
    TrendingUp,
    Megaphone,
    Video,
    Calendar,
    BarChart3,
    Bot,
    Users,
    Heart,
    ShoppingCart,
    Package,
    ChevronDown,
    ChevronUp,
    X
} from 'lucide-react';
import {SearchServiceTags} from "./ServiceOfferingsForm";
import {useSupplierServiceOfferingFormItem} from "../providers/SupplierServiceOfferingFormItemProvider";
import {Button} from "antd";
import {FormInputComponent} from "../../../components/form/Form";
import ServiceOffering from "../../../models/ServiceOffering";

const MAX_TAG_COUNT = 10;
const ServiceCategorySelect = () => {
    const [expandedCategories, setExpandedCategories] = useState(new Set());
    const [visibleCategoriesCount, setVisibleCategoriesCount] = useState(6);

    const {
        profileItem,
        profileIndex,
        onModelChange,
        refresh,
        isEditing
    } = useSupplierServiceOfferingFormItem();
    const serviceData = TB.map(laravel.service_tag_categories || [], cat => {
        return {
            ...cat,
            service_tags: TB.map(cat.service_tags, tag => ({...tag, category: {id: cat.id}}))
        }
    });


    const toggleTagSelection = (tag) => {
        console.log('toggleTagSelection', tag)
        onModelChange({service_tags: TB.xorBy(profileItem.service_tags, [tag], 'id')});
    };

    const removeSelectedTag = (tagId) => {

        onModelChange({service_tags: TB.filter(profileItem.service_tags, tag => tag.id !== tagId)});
    };

    const clearAllTags = () => {
        onModelChange({service_tags: []});
    };

    // Get all tags for finding selected ones
    const allTags = serviceData.flatMap(service => {
        return TB.map(service.service_tags, tag => ({...tag, category_id: service.id}))
    });
    const selectedTags = useMemo(() => {
        return profileItem.service_tags || []
    }, [TB.map(profileItem.service_tags, 'id')]);

    const selectedTagIds = useMemo(() => TB.map(selectedTags, 'id'), [selectedTags]);

    const selectedTagObjects = allTags.filter(tag => selectedTagIds.includes(tag.id));

    const toggleCategory = (categoryId) => {
        setExpandedCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(categoryId)) {
                newSet.delete(categoryId);
            } else {
                newSet.add(categoryId);
            }
            return newSet;
        });
    };

    const showMoreCategories = () => {
        setVisibleCategoriesCount(prev => Math.min(prev + 6, serviceData.length));
    };

    const showLessCategories = () => {
        setVisibleCategoriesCount(6);
    };

    // Get the categories to display
    const categoriesToShow = serviceData.slice(0, visibleCategoriesCount);
    const remainingCategories = serviceData.length - visibleCategoriesCount;

    return (
    <div className="space-y-4">
        <h4 className="text-lg font-semibold">
            Select Matching Services
        </h4>
        <p className="text-sm">
            {/* To get started, choose from Wripple Services below that match your service offering. You can select more than one. */}
            To make sure this service offering shows up in Wripple searches, please select up to {MAX_TAG_COUNT} service tags that best match the offering. These tags come from Wripple's standard service taxonomy and help clients easily find your offerings.
        </p>
        {/* Selected Tags Section */}
        {selectedTags?.length > 0 && (
            <div className=" mt-4 mb-12 p-6 from-slate-50 to-slate-100 bg-gradient-to-br rounded-xl">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                        Selected Services ({selectedTags.length}/{MAX_TAG_COUNT})
                    </h3>
                    <button
                        onClick={clearAllTags}
                        className="text-sm text-red-500 hover:text-red-700 transition-colors duration-200"
                    >
                        Clear All
                    </button>
                </div>
                <div className="flex flex-wrap gap-2">
                    {selectedTagObjects.map((tag) => {
                        const IconComponent = ServiceOffering.iconForServiceCategory(tag.category_id);

                        return (
                            <span
                                key={tag.id}
                                className={TB.mapClass(
                                    `inline-flex items-center gap-2 px-3 py-1.5 bg-${ServiceOffering.colorForServiceCategory(tag.category_id)}-500 text-white rounded-full text-sm font-medium cursor-pointer hover:bg-${ServiceOffering.colorForServiceCategory(tag.category_id)}-600 transition-colors duration-200`
                                )}
                                onClick={() => removeSelectedTag(tag.id)}
                            >
                                {!!IconComponent && <IconComponent className="w-4 h-4 text-white"/>}
                                {tag.name}
                                <X className={`w-3 h-3`} />
                            </span>
                        )
                    })}
                </div>
            </div>
        )}
        {/* Service Tags Search */}
        <div>
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-8">
                <div className="mx-auto">
                    <div className="text-center mb-5">
                        <h1 className="text-lg font-bold text-gray-900 mb-4">Wripple Services</h1>
                        <p className="text-sm text-gray-600 mx-auto">
                            Comprehensive solutions across digital marketing, strategy, and customer experience.
                        </p>
                    </div>



                    <div className="my-4">

                        <SearchServiceTags
                            isDisabled={false}
                            isEditable={true}
                            value={profileItem.service_tags || []}
                            maxCount={MAX_TAG_COUNT}
                            onChange={(updatedTags) => {
                                console.log('cnakjs', updatedTags)
                                onModelChange({
                                    ...profileItem,
                                    service_tags: updatedTags
                                });
                            }}
                        />

                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-2 gap-6">
                        {categoriesToShow.map((service) => {
                            const IconComponent = ServiceOffering.iconForServiceCategory(service.id);
                            const isExpanded = expandedCategories.has(service.id);
                            const visibleTags = isExpanded ? service.service_tags : service.service_tags.slice(0, 4);
                            const remainingCount = service.service_tags.length - 4;

                            return (
                                <div
                                    key={service.id}
                                    className="group relative bg-white rounded-xl shadow-md overflow-hidden"
                                >

                                    <div className="p-6 relative">
                                        <div className="flex items-center justify-start gap-3 mb-4">
                                            <div className={`flex-shrink-0 p-3 rounded-lg bg-${ServiceOffering.colorForServiceCategory(service.id)}-500 `}>
                                                {!!IconComponent && <IconComponent className="w-5 h-5 text-white"/>}
                                            </div>

                                            <h3 className="text-base font-semibold text-gray-900 ">
                                                {service.name}
                                            </h3>
                                        </div>

                                        {/* Service Tags as clickable badges */}
                                        <div className="mb-4">
                                            <div className="flex flex-wrap gap-y-3 gap-x-2">
                                                {visibleTags.map((tag) => {
                                                    const isSelected = TB.find(selectedTags || [], ['id', tag.id]);
                                                    const canSelect = selectedTags.length < MAX_TAG_COUNT || isSelected;
                                                    return (
                                                        <button
                                                            key={tag.id}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (canSelect) {
                                                                    toggleTagSelection(tag);
                                                                }
                                                            }}
                                                            disabled={!canSelect}
                                                            className={TB.mapClass("inline-flex flex-col px-3 py-1 rounded-md text-xs text-left font-medium transition-all duration-200", {
                                                                [`bg-${ServiceOffering.colorForServiceCategory(service.id)}-500 text-white shadow-md transform scale-105`]: isSelected,
                                                                "bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-sm": !isSelected && canSelect,
                                                                "bg-gray-50 text-gray-400 cursor-not-allowed opacity-50": !isSelected && !canSelect,
                                                                "py-2": isExpanded
                                                            })}
                                                        >
                                                            <div className={TB.mapClass("flex flex-row items-center justify-between", {
                                                                "font-semibold mb-1": isExpanded,
                                                            })}>
                                                                {tag.name}
                                                                {isSelected && <span className="ml-1">✓</span>}
                                                            </div>
                                                            {isExpanded && (
                                                                <span className="text-xxs">
                                                                    {tag.description}
                                                                </span>
                                                            )}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>

                                        {/* Expand/Collapse Button */}
                                        {service.service_tags.length > 4 && (
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleCategory(service.id);
                                                }}
                                                className="mt-auto self-center mx-auto flex items-center gap-1 text-sm text-gray-500"
                                            >
                                                {isExpanded ? (
                                                    <>
                                                        Show less
                                                    </>
                                                ) : (
                                                    <>
                                                        Show more
                                                    </>
                                                )}
                                            </button>
                                        )}

                                        {/*<div className="w-0 group-hover:w-full h-0.5 bg-gradient-to-r from-transparent via-gray-300 to-transparent transition-all duration-500 mt-4"></div>*/}
                                    </div>

                                    {/*<div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-gray-200 to-transparent transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>*/}
                                </div>
                            );
                        })}
                    </div>

                    <div className="text-center mx-auto mt-12">
                        {visibleCategoriesCount < serviceData.length ? (
                            <Button
                                type="primary"
                                typeof="button"
                                size="middle"
                                onClick={showMoreCategories}
                            >
                                Show More ({remainingCategories} remaining)
                            </Button>
                        ) : (
                            <Button
                                onClick={showLessCategories}
                                type="default"
                                typeof="button"
                                size="middle"
                            >
                                Show Less
                            </Button>
                        )}

                    </div>
                </div>
            </div>
        </div>
        <FormInputComponent
            type="hidden"
            style={{display: 'none'}}
            fieldName="service_tags"
            value={profileItem.service_tags}
        />
    </div>
    );
};

export default ServiceCategorySelect;